import { useT } from '@hostesa/i18n';
import {
  clearImpersonation,
  selectImpersonatedUserLabel,
  selectImpersonationActive,
  selectImpersonationStartedAt,
  useAppDispatch,
  useAppSelector,
} from '@hostesa/state';
import { Button } from '@hostesa/ui';

/**
 * PERSISTENT impersonation banner.
 *
 * Rendered above all operator content. While an impersonation session is active
 * (driven by @hostesa/state impersonationSlice), this banner is ALWAYS visible
 * so the operator can never forget they are acting on behalf of a hotel user.
 *
 * SECURITY / PII:
 * - The banner reads DISPLAY STATE ONLY. The slice never grants permissions;
 *   the backend enforces every action.
 * - `impersonatedUserLabel` is a non-PII string (e.g. "Hotel Admin — Vila
 *   Rila"). It must never contain emails, phones, or guest identifiers.
 * - Starting impersonation requires `admin:impersonate` (checked by the caller
 *   before dispatching startImpersonation).
 */
export function ImpersonationBanner() {
  const { t } = useT('backoffice');
  const dispatch = useAppDispatch();
  const active = useAppSelector(selectImpersonationActive);
  const label = useAppSelector(selectImpersonatedUserLabel);
  const startedAt = useAppSelector(selectImpersonationStartedAt);

  if (!active) return null;

  return (
    <div
      data-testid="impersonation-banner"
      role="status"
      aria-live="polite"
      className="flex items-center justify-between gap-4 border-b border-[var(--color-alert)] bg-[var(--color-alert)]/10 px-6 py-2 text-sm text-[var(--color-charcoal)]"
    >
      <span data-testid="impersonation-banner-label" className="flex flex-wrap items-center gap-2">
        <strong className="font-[var(--font-display)] text-[var(--color-alert)]">
          {t('impersonation.bannerLabel')}
        </strong>
        <span>
          {t('impersonation.actingAs')} <strong>{label}</strong>
          {startedAt ? (
            <>
              {' '}
              {t('impersonation.since')} {new Date(startedAt).toLocaleString()}
            </>
          ) : null}
        </span>
      </span>
      <Button
        data-testid="impersonation-stop-btn"
        variant="secondary"
        size="sm"
        onClick={() => dispatch(clearImpersonation())}
      >
        {t('impersonation.stop')}
      </Button>
    </div>
  );
}
